import { ReactElement } from 'react'
import { useQuery } from 'urql'
import { DEBUG_TRACES_QUERY } from 'app/components/contacts/inbox/graphql'
import { DebugTraceCard } from 'app/components/contacts/inbox/DebugTraceCard'
import Drawer from 'app/components/drawer/Drawer'
import Spinner from 'app/components/Spinner'
import { Button } from 'app/components/ui/button'
import { formatDate } from 'app/date.utils'
import type { AiConversationTurn } from 'app/generated/graphql'
import { inboxThreadHref } from 'app/table-columns/sms-agent-ops'
import { cn } from 'app/lib/utils'
import type { SmsOpsTurnRow } from './graphql'

/**
 * Every AI turn on the selected turn's thread, as the inbox's debug mode shows them,
 * with the selected (errored) turn outlined. Loads the thread's turns with the inbox's
 * `DEBUG_TRACES_QUERY`, which carries the prompt the Ops list leaves out.
 */
export function AiTurnDrawer({
  turn,
  locationName,
  onClose,
  onOpenThread,
}: {
  turn?: SmsOpsTurnRow
  locationName: string
  onClose: () => void
  onOpenThread: (href: string) => void
}): ReactElement {
  // Typed like the inbox's own read of this query: `DebugTraceCard` takes the model type.
  const [{ data, fetching }] = useQuery<{ aiConversationTurns: { hits: AiConversationTurn[] } }>({
    query: DEBUG_TRACES_QUERY,
    variables: {
      threadId: turn?.threadId,
      sort: { field: `createdDate`, direction: `ASC` },
      pagination: { limit: 500 },
    },
    pause: !turn,
  })
  const turns = data?.aiConversationTurns.hits ?? []
  const threadHref = inboxThreadHref(turn?.locationId, turn?.threadId)

  return (
    <Drawer
      open={!!turn}
      title="AI Reply Details"
      canExpand={false}
      onClose={onClose}
      leftContent={
        <div className="flex w-full flex-col gap-4 p-4 md:w-170">
          {turn && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-body-sm text-subtle">
                <span className="text-ui-sm text-strong">{locationName || `Unknown location`}</span>
                {` · ${formatDate({ value: turn.createdDate, useLocalUtc: true })}`}
              </div>
              {threadHref && (
                <Button variant="tertiary" size="sm" onClick={() => onOpenThread(threadHref)}>
                  Open in Inbox
                </Button>
              )}
            </div>
          )}
          {fetching ? (
            <div className="flex h-40 items-center justify-center">
              <Spinner size={16} />
            </div>
          ) : (
            turns.map((threadTurn) => (
              <div
                key={threadTurn.id}
                className={cn(`rounded-lg`, threadTurn.id === turn?.id && `ring-2 ring-red-500 ring-offset-2`)}
              >
                <DebugTraceCard turn={threadTurn} fullWidth />
              </div>
            ))
          )}
        </div>
      }
    />
  )
}
